import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = import.meta.env.VITE_TARRY_API || '';
const BATCH_INTERVAL = 5000; // flush every 5 seconds
const STORAGE_KEY = 'tarry_session_id';

interface AnalyticsEvent {
  session_id: string | null;
  event_type: 'page_view' | 'click';
  page: string;
  element?: string;
  element_tag?: string;
  referrer?: string;
  user_agent?: string;
  screen_width?: number;
}

let eventQueue: AnalyticsEvent[] = [];

function getSessionId(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

function flush() {
  if (eventQueue.length === 0) return;
  const batch = [...eventQueue];
  eventQueue = [];

  // Fire-and-forget — non-blocking
  fetch(`${API_URL}/api/analytics/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events: batch }),
    keepalive: true, // survives page unload
  }).catch(() => {
    // If it fails, push events back for next flush
    eventQueue.push(...batch);
  });
}

function trackEvent(event: AnalyticsEvent) {
  eventQueue.push(event);
  // Flush immediately if queue gets large
  if (eventQueue.length >= 20) flush();
}

export function useAnalytics() {
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const lastPage = useRef<string>('');

  // Track page views on route change
  useEffect(() => {
    if (location.pathname === lastPage.current) return;
    lastPage.current = location.pathname;

    trackEvent({
      session_id: getSessionId(),
      event_type: 'page_view',
      page: location.pathname,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
    });
  }, [location.pathname]);

  // Click tracking — delegate from document
  const handleClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const clickable = target.closest('a, button, [role="button"], [data-track]');
    if (!clickable) return;

    const label =
      clickable.getAttribute('data-track') ||
      clickable.getAttribute('aria-label') ||
      clickable.textContent?.trim().substring(0, 80) ||
      'unknown';

    trackEvent({
      session_id: getSessionId(),
      event_type: 'click',
      page: location.pathname,
      element: label,
      element_tag: clickable.tagName,
      screen_width: window.innerWidth,
    });
  }, [location.pathname]);

  useEffect(() => {
    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, [handleClick]);

  // Periodic flush
  useEffect(() => {
    timerRef.current = setInterval(flush, BATCH_INTERVAL);
    return () => {
      clearInterval(timerRef.current);
      flush(); // flush remaining on unmount
    };
  }, []);

  // Flush on page unload
  useEffect(() => {
    const onUnload = () => flush();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
}
